<!-- `||` and not `??`: an empty label would leave the slides unnamed. -->
@let slideIndicatorLabel =
  config.config.slideIndicatorLabel || 'Slide {variable} of {variable}';
<section
  role="region"
  class="carousel-container"
  [attr.aria-label]="config.config.sectionAriaLabel"
  aria-roledescription="carousel"
>
  <!-- Live region for screen reader announcements -->
  <div class="sr-only" aria-live="polite" aria-atomic="true">
    {{
      slideIndicatorLabel
        | replaceVars: activeSlide() + 1
        | replaceVars: items().length
    }}
  </div>

  <ul
    b2cSwipe
    #carousel
    role="presentation"
    (swipeStart)="panStart()"
    class="b2c-carousel-inner"
    (swipeEnd)="panEnd($event)"
    (swipeMove)="panMove($event)"
  >
    <!-- Clone of last slide (for infinite loop) -->
    @if (items().length > 1 && isLooping) {
      <li class="carousel-item clone" aria-hidden="true" inert>
        <b2c-carousel-item [stepContent]="items()[items().length - 1]" />
      </li>
    }

    <!-- Carousel Items -->
    @for (item of items(); track $index) {
      <li
        #carouselItem
        role="tabpanel"
        class="carousel-item"
        aria-roledescription="slide"
        [attr.id]="'slide-panel-' + instanceId + '-' + $index"
        [class.active]="$index === activeSlide()"
        [attr.aria-hidden]="$index !== activeSlide()"
        [attr.aria-label]="
          slideIndicatorLabel
            | replaceVars: $index + 1
            | replaceVars: items().length
        "
      >
        <b2c-carousel-item [stepContent]="item" />
      </li>
    }

    <!-- Clone of first slide (for infinite loop) -->
    @if (items().length > 1 && isLooping) {
      <li class="carousel-item clone" aria-hidden="true" inert>
        <b2c-carousel-item [stepContent]="items()[0]" />
      </li>
    }
  </ul>

  <!-- Carousel Controls -->
  <div
    class="carousel-controls"
    role="group"
    [attr.aria-label]="config.config.controlsAriaLabel"
  >
    <button
      type="button"
      class="left control"
      (click)="goToPrevSlide()"
      [disabled]="!canGoPrev()"
      [attr.aria-label]="config.config.controls.leftIcon.alternativeText"
    >
      <!-- reduce noises with empty alt here -->
      <img
        [alt]="''"
        aria-hidden="true"
        class="responsive-width"
        [src]="config.config.controls.leftIcon.imageUrl"
      />
    </button>
    <button
      type="button"
      class="right control"
      (click)="goToNextSlide()"
      [disabled]="!canGoNext()"
      [attr.aria-label]="config.config.controls.rightIcon.alternativeText"
    >
      <!-- reduce noises with empty alt here -->
      <img
        [alt]="''"
        aria-hidden="true"
        class="responsive-width"
        [src]="config.config.controls.rightIcon.imageUrl"
      />
    </button>
  </div>
</section>

<!-- Carousel Dots Pagination -->
<div
  class="carousel-pagination"
  role="tablist"
  [attr.aria-label]="config.config.paginationAriaLabel"
>
  @for (item of items(); track $index) {
    <button
      role="tab"
      type="button"
      class="bullet"
      (click)="goToSlideByIndex($index)"
      [attr.id]="'slide-tab-' + instanceId + '-' + $index"
      [attr.aria-controls]="'slide-panel-' + instanceId + '-' + $index"
      [attr.aria-selected]="$index === activeSlide()"
      [attr.tabindex]="$index === activeSlide() ? 0 : -1"
      [style.backgroundColor]="
        $index === activeSlide()
          ? config.config.dots.activeBg
          : config.config.dots.inactiveBg
      "
      [attr.aria-label]="
        config.config.dots.ariaLabel
          | replaceVars: $index + 1
          | replaceVars: items().length
      "
    ></button>
  }
</div>
